import { React, useEffect, useState } from "react";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import {
	Form,
	Input,
	DatePicker,
	TimePicker,
	Select,
	Button,
	message,
	Card,
	ConfigProvider,
	Radio,
	Space,
	Layout,
} from "antd";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore.js";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import { useNavigate } from "react-router-dom";
import RentSanctuaryNavigation from "./navigation/RentSanctuaryNavigation";
import useIsMobile from "../../../hooks/useIsMobile";

dayjs.locale("ko");
dayjs.extend(isSameOrBefore);

const { Content } = Layout;

const { Option } = Select;
const { TextArea } = Input;

/** 성전 목록에 없는 장소 등록 시 Select 값 (백엔드에는 sanctuary_room=null) */
const SANCTUARY_OTHER_VALUE = "__OTHER__";

const BulkRentSchedule = () => {
	const isMobile = useIsMobile();
	const [form] = Form.useForm();
	const sanctuaryRoomChoice = Form.useWatch("sanctuary_room", form);
	const [loading, setLoading] = useState(false);
	const [sanctuaryRoomList, setSanctuaryRoomList] = useState([]);
	const [repeatType, setRepeatType] = useState("none");
	const navigate = useNavigate();

	useEffect(() => {
		getSanctuaryRoomList();
	}, []);

	const getSanctuaryRoomList = async () => {
		try {
			const response = await useAxios.get("/utils/sanctuary/list/");
			if (response.status === 200) {
				let sanctuaryRoomList = response.data;
				sanctuaryRoomList.sort((a, b) => {
					if (a.sanctuary_name === b.sanctuary_name) {
						return a.room_name.localeCompare(b.room_name);
					}
					return a.sanctuary_name.localeCompare(b.sanctuary_name);
				});
				setSanctuaryRoomList(sanctuaryRoomList);
			}
		} catch (error) {
			message.error("성전 목록을 불러오는데 실패했습니다.");
		}
	};

	const generateDates = (startDate, endDate, repeatType) => {
		const dates = [];
		let currentDate = dayjs(startDate);

		while (currentDate.isSameOrBefore(endDate, "day")) {
			if (repeatType === "none") {
				dates.push(currentDate);
				break;
			} else if (repeatType === "daily") {
				dates.push(currentDate);
				currentDate = currentDate.add(1, "day");
			} else if (repeatType === "weekly") {
				dates.push(currentDate);
				currentDate = currentDate.add(1, "week");
			}
		}

		return dates;
	};

	const onFinish = async (values) => {
		setLoading(true);
		try {
			// Validate dates
			if (!values.startDate || !values.endDate) {
				message.error("시작일과 종료일을 모두 선택해주세요.");
				setLoading(false);
				return;
			}

			if (values.endDate.isBefore(values.startDate)) {
				message.error("종료일은 시작일보다 이후여야 합니다.");
				setLoading(false);
				return;
			}

			// Validate time
			if (values.endTime <= values.startTime) {
				message.error("종료 시간은 시작 시간보다 크게 설정해주세요.");
				setLoading(false);
				return;
			}

			// Generate dates based on repeat type
			const dates = generateDates(
				values.startDate,
				values.endDate,
				repeatType
			);

			let sanctuary_room = values.sanctuary_room;
			let custom_sanctuary_name = "";
			if (sanctuary_room === SANCTUARY_OTHER_VALUE) {
				sanctuary_room = null;
				custom_sanctuary_name = (values.custom_sanctuary_name || "").trim();
			}

			// Create applications for each date
			const applications = dates.map((date) => {
				const startDateTime = dayjs(date)
					.hour(values.startTime.hour())
					.minute(values.startTime.minute())
					.second(0);

				const endDateTime = dayjs(date)
					.hour(values.endTime.hour())
					.minute(values.endTime.minute())
					.second(0);

				return {
					sanctuary_room,
					custom_sanctuary_name,
					start_datetime: startDateTime.format("YYYY-MM-DD HH:mm:ss"),
					end_datetime: endDateTime.format("YYYY-MM-DD HH:mm:ss"),
					rent_team: values.rent_team,
					rent_for: values.rent_for,
					representative: values.representative,
					representative_tel: values.representative_tel,
					status: "승인", // 관리자가 일괄 등록하는 경우 바로 승인 상태로
				};
			});

			// Send bulk request
			const response = await useAxios.post(
				"/utils/rental/sanctuary/bulk-create/",
				{
					applications: applications,
				}
			);

			if (response.status === 201) {
				message.success(
					`성전 대여 일정 ${applications.length}건이 등록되었습니다.`
				);
				form.resetFields();
				setRepeatType("none");
                navigate("/utils/rent-sanctuary/view-rental-sanctuary-schedule");
			} else {
				message.error(
					response.data.error || "일괄 등록 처리 중 오류가 발생했습니다."
				);
			}
		} catch (error) {
			if (error.response && error.response.data.error) {
				message.error(error.response.data.error);
			} else {
				message.error("일괄 등록 처리 중 오류가 발생했습니다.");
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="성전 대여 일괄 등록" />
			<Layout className="member-layout">
				<RentSanctuaryNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="utils-rent_sanctuary__container">
							<div className="utils-rent_sanctuary__header">
								<h1 className="utils-rent_sanctuary__header__title">
									성전 대여 일괄 등록
								</h1>
							</div>
							<div className="utils-rent_sanctuary__content-container">
								<Card className="utils-rent_sanctuary__form-card">
									<Form
										form={form}
										layout="vertical"
										onFinish={onFinish}
										initialValues={{
											startDate: dayjs().startOf("day"),
											endDate: dayjs().startOf("day"),
											startTime: dayjs().startOf("day").hour(9).minute(0),
											endTime: dayjs().startOf("day").hour(10).minute(0),
										}}>
										<Form.Item
											name="sanctuary_room"
											label="성전 선택"
											rules={[
												{
													required: true,
													message: "성전을 선택해주세요",
												},
											]}>
											<Select placeholder="성전을 선택하세요">
												{sanctuaryRoomList.map((sanctuaryRoom) => (
													<Option key={sanctuaryRoom.id} value={sanctuaryRoom.id}>
														{sanctuaryRoom.room_name === "-" ?
															sanctuaryRoom.sanctuary_name :
															sanctuaryRoom.sanctuary_name + " " + sanctuaryRoom.room_name}
													</Option>
												))}
												<Option key="__OTHER__" value={SANCTUARY_OTHER_VALUE}>
													기타
												</Option>
											</Select>
										</Form.Item>

										{sanctuaryRoomChoice === SANCTUARY_OTHER_VALUE && (
											<Form.Item
												name="custom_sanctuary_name"
												label="성전 이름"
												rules={[
													{
														required: true,
														message: "성전 이름을 입력해주세요",
													},
													{
														validator(_, value) {
															if (!(value && String(value).trim())) {
																return Promise.reject(
																	new Error("성전 이름을 입력해주세요"),
																);
															}
															return Promise.resolve();
														},
													},
												]}>
												<Input placeholder="등록할 성전 이름을 입력하세요" />
											</Form.Item>
										)}

										<Form.Item
											name="rent_team"
											label="교구/기관명"
											rules={[
												{
													required: true,
													message: "교구/기관명을 입력해주세요",
												},
											]}>
											<Input placeholder="교구/기관명을 입력하세요" />
										</Form.Item>

										<Form.Item
											name="rent_for"
											label="사용 목적"
											rules={[
												{
													required: true,
													message: "사용 목적을 입력해주세요",
												},
											]}>
											<TextArea rows={4} placeholder="사용 목적을 입력하세요" />
										</Form.Item>

										<Form.Item
											name="representative"
											label="신청자 이름"
											rules={[
												{
													required: true,
													message: "신청자 이름을 입력해주세요",
												},
											]}>
											<Input placeholder="신청자 이름을 입력하세요" />
										</Form.Item>

										<Form.Item
											name="representative_tel"
											label="신청자 연락처"
											rules={[
												{
													required: true,
													message: "신청자 연락처를 입력해주세요",
												},
												{
													pattern: /^[0-9-]+$/,
													message: "숫자와 하이픈(-)만 입력 가능합니다",
												},
											]}>
											<Input placeholder="연락처를 입력하세요 (예: 010-1234-5678)" />
										</Form.Item>

										<Form.Item
											label="반복 설정"
											required>
											<Radio.Group
												value={repeatType}
												onChange={(e) => setRepeatType(e.target.value)}>
												<Radio value="none">반복 없음</Radio>
												<Radio value="daily">매일</Radio>
												<Radio value="weekly">매주</Radio>
											</Radio.Group>
										</Form.Item>

										<Space direction="vertical" style={{ width: "100%" }}>
											<Form.Item
												name="startDate"
												label="시작일"
												rules={[
													{
														required: true,
														message: "시작일을 선택해주세요",
													},
												]}>
												<DatePicker
													inputReadOnly={isMobile}
													style={{ width: "100%" }}
													format="YYYY-MM-DD"
													allowClear={false}
												/>
											</Form.Item>

											<Form.Item
												name="endDate"
												label="종료일"
												rules={[
													{
														required: true,
														message: "종료일을 선택해주세요",
													},
												]}>
												<DatePicker
													inputReadOnly={isMobile}
													style={{ width: "100%" }}
													format="YYYY-MM-DD"
													allowClear={false}
												/>
											</Form.Item>
										</Space>

										<Space direction="vertical" style={{ width: "100%" }}>
											<Form.Item
												name="startTime"
												label="시작 시간"
												rules={[
													{
														required: true,
														message: "시작 시간을 선택해주세요",
													},
												]}>
												<TimePicker
													inputReadOnly={isMobile}
													style={{ width: "100%" }}
													format="HH:mm"
													minuteStep={30}
													allowClear={false}
												/>
											</Form.Item>

											<Form.Item
												name="endTime"
												label="종료 시간"
												rules={[
													{
														required: true,
														message: "종료 시간을 선택해주세요",
													},
												]}>
												<TimePicker
													inputReadOnly={isMobile}
													style={{ width: "100%" }}
													format="HH:mm"
													minuteStep={30}
													allowClear={false}
												/>
											</Form.Item>
										</Space>

										<Form.Item>
											<Button
												type="primary"
												htmlType="submit"
												loading={loading}
												block>
												일괄 등록하기
											</Button>
										</Form.Item>
									</Form>
								</Card>
							</div>
						</div>
					</Content>
				</Layout>
			</Layout>
		</ConfigProvider>
	);
};

export default BulkRentSchedule;
